import { forwardRef, Fragment, useEffect, useRef, useState } from 'react';
import type { PropsWithoutRef, Ref, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { rgba } from 'polished';

import { useElement, useConsolidatedRef, useDirection } from '../../hooks';
import { defaultThemeProp } from '../../theme';
import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import { debounce, tryCatch } from '../../utils';
import Button, { StyledButton } from '../Button';
import Flex from '../Flex';
import Link, { StyledLink } from '../Link';
import Icon, { StyledIcon, registerIcon } from '../Icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import MenuButton from '../MenuButton';
import Text, { StyledText } from '../Text';
import type { MenuItemProps } from '../Menu';
import Tooltip from '../Tooltip/Tooltip';

registerIcon(caretLeftIcon, caretRightIcon);

export interface BreadcrumbsProps extends BaseProps, NoChildrenProp {
  /** A set of actions representing the chronological hierarchy of pages or locations leading to the current view. */
  path: MenuItemProps[];
  /**
   * Renders only the leaf node outside the menu list.
   * @default false
   * Note: Disables variable length on resize.
   */
  leafOnly?: boolean;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const StyledSeparator: typeof Icon = styled(Icon)(({ theme }) => {
  const color = tryCatch(() =>
    rgba(theme.base.palette['foreground-color'], theme.base.transparency['transparent-2'])
  );

  return css`
    color: ${color};
  `;
});

export const StyledBreadcrumbs = styled.div<{
  oneItemRemaining: boolean;
  fixedLength: boolean;
}>(
  ({ oneItemRemaining, fixedLength }) => css`
    white-space: nowrap;
    overflow: visible;
    visibility: ${fixedLength ? undefined : 'hidden'};

    ${StyledIcon} {
      width: 1em;
      height: 1em;
    }

    ${StyledLink},
    ${StyledButton},
    ${StyledText} {
      flex-shrink: 1;

      &:last-child {
        ${oneItemRemaining
          ? css`
              flex-shrink: 1;
              text-overflow: ellipsis;
              overflow: hidden;
            `
          : css`
              flex-shrink: 0;
            `}
      }
    }
  `
);

const getEllipsisStyles = ({ theme }: { theme: DefaultTheme }) => {
  return css`
    min-width: ${theme.base['content-width'].xs};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
};

const StyledEllipsizedLink = styled(Link)(getEllipsisStyles);

StyledEllipsizedLink.defaultProps = defaultThemeProp;

const StyledEllipsizedButton = styled(Button)(getEllipsisStyles);

StyledEllipsizedButton.defaultProps = defaultThemeProp;

const StyledEllipsizedText = styled(Text)(getEllipsisStyles);

StyledEllipsizedText.defaultProps = defaultThemeProp;

const Breadcrumbs: ForwardRefForwardPropsComponent<BreadcrumbsProps> = forwardRef(
  function Breadcrumbs(
    { path, leafOnly = false, ...restProps }: PropsWithoutRef<BreadcrumbsProps>,
    ref: BreadcrumbsProps['ref']
  ) {
    const nodeRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    const [breadcrumbsEl, setBreadcrumbsEl] = useElement<HTMLDivElement>();
    const [numMenuItems, setNumMenuItems] = useState(leafOnly ? Math.max(path.length - 1, 0) : 0);
    const minInlineWidth = useRef(0);

    const breadcrumbRef = useConsolidatedRef(setBreadcrumbsEl, ref);

    // Update menu based on length
    useEffect(() => {
      if (leafOnly) {
        setNumMenuItems(Math.max(path.length - 1, 0));
      } else {
        setNumMenuItems(0);
      }
    }, [path, leafOnly]);

    // Manage Observers
    useEffect(() => {
      const debouncedResize = debounce((entries: ResizeObserverEntry[]) => {
        if (entries[0].contentRect.width > minInlineWidth.current) {
          setNumMenuItems(curr => {
            if (curr === 0) return curr;
            return curr - 1;
          });
        }
      }, 100);

      const resizeObserver = new ResizeObserver(debouncedResize);

      const intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries[0].intersectionRatio < 1 && numMenuItems < path.length - 1) {
            if (entries[0].rootBounds) minInlineWidth.current = entries[0].rootBounds.width + 1;
            setNumMenuItems(curr => {
              if (curr === path.length - 1) return curr;
              return curr + 1;
            });
          } else if (breadcrumbsEl) breadcrumbsEl.style.visibility = 'visible';
        },
        { root: breadcrumbsEl, threshold: 1 }
      );

      if (!leafOnly && breadcrumbsEl && breadcrumbsEl.lastChild) {
        intersectionObserver.observe(breadcrumbsEl.lastChild as Element);
        resizeObserver.observe(breadcrumbsEl);
      }

      return () => {
        intersectionObserver.disconnect();
        resizeObserver.disconnect();
      };
    }, [breadcrumbsEl, numMenuItems, leafOnly]);

    const { end } = useDirection();

    return (
      <Flex
        {...restProps}
        container={{ gap: 0.5, alignItems: 'center' }}
        as={StyledBreadcrumbs}
        oneItemRemaining={numMenuItems === path.length - 1}
        fixedLength={leafOnly}
        ref={breadcrumbRef}
      >
        {numMenuItems > 0 && (
          <>
            <MenuButton
              text='Links'
              iconOnly
              variant='link'
              icon='folder-hierarchy-solid'
              menu={{ items: path.slice(0, numMenuItems).reverse() }}
            />
            <StyledSeparator name={`caret-${end}`} />
          </>
        )}
        {path.slice(numMenuItems).map((crumb, index) => {
          const { id, primary, visual, href, onClick, ...restCrumbProps } = crumb;
          let Comp;
          if (href)
            Comp = (
              <Link
                key={id}
                href={href}
                as={primary.length >= 20 ? StyledEllipsizedLink : undefined}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  onClick?.(id, e);
                }}
                {...restCrumbProps}
                ref={el => {
                  nodeRefs.current[id] = el;
                }}
              >
                {visual} {primary}
              </Link>
            );
          else if (onClick)
            Comp = (
              <Button
                key={id}
                variant='link'
                as={primary.length >= 20 ? StyledEllipsizedButton : undefined}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  onClick(id, e);
                }}
                {...restCrumbProps}
                ref={el => {
                  nodeRefs.current[id] = el;
                }}
              >
                {visual} {primary}
              </Button>
            );
          else
            Comp = (
              <Text
                key={id}
                as={primary.length >= 20 ? StyledEllipsizedText : undefined}
                {...restCrumbProps}
                ref={el => {
                  nodeRefs.current[id] = el;
                }}
              >
                {visual} {primary}
              </Text>
            );

          if (index === path.slice(numMenuItems).length - 1) return Comp;

          return (
            <Fragment key={id}>
              {Comp}
              <Tooltip target={nodeRefs.current[id]} smart>
                {primary}
              </Tooltip>
              <StyledSeparator name={`caret-${end}`} />
            </Fragment>
          );
        })}
      </Flex>
    );
  }
);

export default Breadcrumbs;
